import Velocity from 'velocity-animate';

import Component from '../component/component';
import Helpers, { debounce, DebouncedFunction } from '../helpers/helpers';

import './image-zoom.scss';

interface IImageZoomSettings {
    contentSelector: string;
    imageSelector: string;
    tallModifier: string;
    wideModifier: string;
}

interface IImageZoomMousePosition {
    x: number | null;
    y: number | null;
}

export default class ImageZoom extends Component {
    static initSelector: string = '.js-image-zoom';

    containerOffset: JQuery.Coordinates;
    containerWidth: number;
    containerHeight: number;
    content: JQuery;
    debouncedResizeHandler: DebouncedFunction<() => void>;
    draggingStarted: boolean;
    image: JQuery<HTMLImageElement>;
    imageLoaded: boolean;
    imageWidth: number;
    imageHeight: number;
    isScrolling: boolean = false;
    lastMousePosition: IImageZoomMousePosition;
    settings: IImageZoomSettings;
    tabLink: JQuery;

    constructor(target: HTMLElement) {
        super(target);

        this.settings = {
            contentSelector: '.image-zoom__content',
            imageSelector: '.image-zoom__image',
            tallModifier: 'image-zoom--tall',
            wideModifier: 'image-zoom--wide',
        };

        this.tabLink = this.element.find('.tabs__nav-link');
        this.content = this.element.find(this.settings.contentSelector);
        this.draggingStarted = false;
        this.image = this.element.find(this.settings.imageSelector) as JQuery<HTMLImageElement>;
        this.lastMousePosition = {
            x: null,
            y: null,
        };
        this.imageLoaded = false;

        if (this.element.is(':visible')) {
            this.init();
        } else {
            $(document).on('enhance.zoom', (): void => {
                this.init();
            });
        }
    }

    init(): void {
        // Check whether image is cached or wait for the image to load:
        if (this.image.length > 0) {
            if (this.image.get(0).complete) {
                this.onImageLoaded();
            } else {
                this.image.on('load', (): void => {
                    this.onImageLoaded();
                });
            }
        } else {
            this.onImageLoaded();
        }

        this.element.on('mousedown touchstart', this.onMouseDown.bind(this));
        this.element.on('mouseup touchend', this.onMouseUp.bind(this));
        this.element.on('mousemove touchmove', this.onMouseMove.bind(this));

        this.tabLink.on('click', () => {
            this.resetPosition();

            setTimeout(() => {
                this.recalculateSizes();
            }, 300);
        });
    }

    recalculateSizes(): void {
        this.imageWidth = this.image.length > 0 ? this.image.width() : this.content.width();
        this.imageHeight = this.image.length > 0 ? this.image.height() : this.content.height();
    }

    resetPosition(): void {
        this.content.animate({
            left: 0,
            top: 0,
        }, 300, 'linear');

        this.image.animate({
            left: 0,
            top: 0,
        }, 300, 'linear');
    }

    onImageLoaded(): void {
        this.imageLoaded = true;

        if (this.image.length > 0) {
            if (this.image.get(0).naturalWidth > this.image.get(0).naturalHeight * 2) {
                this.element.addClass(this.settings.wideModifier);
            }

            if (this.image.get(0).naturalHeight > this.image.get(0).naturalWidth * 2) {
                this.element.addClass(this.settings.tallModifier);
            }
        } else {
            if (this.content.innerWidth() > this.content.innerHeight() * 2) {
                this.element.addClass(this.settings.wideModifier);
            }

            if (this.content.innerHeight() > this.content.innerWidth() * 2) {
                this.element.addClass(this.settings.tallModifier);
            }
        }

        this.onResize();
        this.debouncedResizeHandler = debounce(this.onResize.bind(this), 50);
        $(window).on('resize', this.debouncedResizeHandler);

        this.element.on('mousewheel', this.onScroll.bind(this));
    }

    onResize(): void {
        this.prepareContent();
        this.containerWidth = this.element.outerWidth();
        this.containerHeight = this.element.outerHeight();
        this.containerOffset = this.element.offset();
        this.imageWidth = this.image.length > 0 ? this.image.width() : this.content.width();
        this.imageHeight = this.image.length > 0 ? this.image.height() : this.content.height();
    }

    prepareContent(): void {
        if (window.innerHeight > window.innerWidth) {
            // Portrait
            this.content.css({
                minWidth: window.innerHeight * 1.2 + 'px',
            });
        } else {
            // Landscape
            this.content.css({
                minWidth: window.innerWidth * 1.2 + 'px',
            });
        }
    }

    onScroll(e: JQuery.ScrollEvent): void {
        if (e.originalEvent && !this.isScrolling) {
            const el: JQuery = this.image.length > 0 ? this.image : this.content;

            const changeY: number = (e.originalEvent as WheelEvent).deltaY * -1 * 2.5; // Increase scroll sensitivity 2.5x
            const changeX: number = (e.originalEvent as WheelEvent).deltaX * -1 * 2.5; // Increase scroll sensitivity 2.5x

            Velocity(el, {
                left: this.getLeftNew(changeX),
                top: this.getTopNew(changeY),
            }, {
                begin: this.onScrollBegin.bind(this),
                complete: this.onScrollComplete.bind(this),
                duration: 200,
                easing: 'linear',
            });
        }
    }

    onScrollBegin(): void {
        this.isScrolling = true;
    }

    onScrollComplete(): void {
        this.isScrolling = false;
    }

    onMouseDown(e: JQuery.MouseDownEvent | JQuery.TouchStartEvent): void {
        // Image should be loaded before it can be dragged
        if (this.imageLoaded) {
            this.draggingStarted = true;
            let touchEvent: JQuery.TouchStartEvent;

            if (Helpers.isMobileDevice) {
                touchEvent = e as JQuery.TouchStartEvent;
            }

            // Save mouse position:
            this.lastMousePosition = {
                x: ((Helpers.isMobileDevice && touchEvent.originalEvent.touches) ? touchEvent.originalEvent.touches[0].pageX : e.pageX) - this.containerOffset.left,
                y: ((Helpers.isMobileDevice && touchEvent.originalEvent.touches) ? touchEvent.originalEvent.touches[0].pageY : e.pageY) - this.containerOffset.top,
            };
        }
    }

    onMouseUp(): void {
        this.draggingStarted = false;
    }

    onMouseMove(e: JQuery.MouseMoveEvent | JQuery.TouchMoveEvent): void {
        if (this.draggingStarted && !this.isScrolling) {
            const el: JQuery = this.image.length > 0 ? this.image : this.content;
            const currentMousePosition: IImageZoomMousePosition = {
                x: (Helpers.isMobileDevice ? (e as JQuery.TouchMoveEvent).originalEvent.touches[0].pageX : e.pageX) - this.containerOffset.left,
                y: (Helpers.isMobileDevice ? (e as JQuery.TouchMoveEvent).originalEvent.touches[0].pageY : e.pageY) - this.containerOffset.top,
            };
            const changeX: number = currentMousePosition.x - this.lastMousePosition.x;
            const changeY: number = currentMousePosition.y - this.lastMousePosition.y;

            // Save mouse position:
            this.lastMousePosition = currentMousePosition;

            Velocity(el, {
                left: this.getLeftNew(changeX),
                top: this.getTopNew(changeY),
            }, {
                begin: this.onScrollBegin.bind(this),
                complete: this.onScrollComplete.bind(this),
                duration: 5,
                easing: 'linear',
            });
        }
    }

    getTopNew(change: number): number {
        const el: JQuery = this.image.length > 0 ? this.image : this.content;

        if (this.element.height() < el.height()) {
            const imgTop: number = parseInt(el.css('top'), 10);
            const imageTopMargin: number = parseInt(el.css('margin-top'), 10);
            const imageBottomMargin: number = parseInt(el.css('margin-bottom'), 10);
            const containerTopPadding: number = parseInt(this.element.css('padding-top'), 10);
            const containerBottomPadding: number = parseInt(this.element.css('padding-bottom'), 10);
            const topValidation: number = this.containerHeight - this.imageHeight - imageTopMargin - imageBottomMargin - containerTopPadding - containerBottomPadding;
            let imgTopNew: number = imgTop + change;

            // Validate top do not fall outside the image:
            if (imgTopNew > 0) {
                imgTopNew = 0;
            }

            if (imgTopNew < topValidation) {
                imgTopNew = topValidation;
            }

            return imgTopNew;
        } else {
            return 0;
        }
    }

    getLeftNew(change: number): number {
        const el: JQuery = this.image.length > 0 ? this.image : this.content;

        if (this.element.width() < el.width()) {
            const imgLeft: number = parseInt(el.css('left'), 10);
            const imageRightMargin: number = parseInt(el.css('margin-right'), 10);
            const imageLeftMargin: number = parseInt(el.css('margin-left'), 10);
            const containerRightPadding: number = parseInt(this.element.css('padding-right'), 10);
            const containerLeftPadding: number = parseInt(this.element.css('padding-left'), 10);
            const leftValidation: number = this.containerWidth - this.imageWidth - imageLeftMargin - imageRightMargin - containerRightPadding - containerLeftPadding;
            let imgLeftNew: number = imgLeft + change;

            // Validate left do not fall outside the image:
            if (imgLeftNew > 0) {
                imgLeftNew = 0;
            }

            if (imgLeftNew < leftValidation) {
                imgLeftNew = leftValidation;
            }

            return imgLeftNew;
        } else {
            return 0;
        }
    }
}
